import axios from "axios";
import React, { useEffect, useState } from "react";
import ChapterWiseReportPieChart from "./components/ChapterWiseReportPieChart";
import { CHAPTER_WISE_ONE_TO_ONE_REPORTS_LIST } from "../../routes/routes";
import { NOPAGINATE } from "../../constant/constant";
import { hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import { toast } from "react-toastify";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import {
  styleExcelHeaderRow,
  styleExcelSummaryRow,
  styleExcelTitleRow,
} from "../../utils/excelStyles";
import { Link } from "react-router-dom";

const ChapterWiseOnetoOneReportsList = () => {
  const { hasPermission } = usePermission();
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [btnExcelDisabled, setBtnExcelDisabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(CHAPTER_WISE_ONE_TO_ONE_REPORTS_LIST, {
        params: {
          type: NOPAGINATE,
          isAdmin: 1,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          const responseData = response.data.data;
          setData(responseData.chapter_wise_one_to_one || []);
          setTotalCount(responseData.total_onetoone_count || 0);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const columns = [{ header: "Sl.No.", key: "sno" }];
  if (hasRole([1])) {
    columns.push({ header: "Country", key: "country_name" });
  }
  if (hasRole([1, 6])) {
    columns.push({ header: "State", key: "state_name" });
  }
  if (hasRole([1, 6, 7])) {
    columns.push({ header: "Region", key: "region_name" });
  }
  columns.push({ header: "Chapter Name", key: "chapter_name" });
  columns.push({ header: "Total RnR Count", key: "onetoone_count" });

  const workSheetName = `Chapter Wise RnR Reports (Overall)`;
  const title = workSheetName;
  const fileName = "Chapter Wise RnR Reports";
  const workbook = new Excel.Workbook();

  const exportAsExcel = async () => {
    try {
      const worksheet = workbook.addWorksheet(fileName);
      worksheet.columns = columns;
      worksheet.insertRow(1, []);

      const totalCols = columns.length || 1;
      const lastColLetter = String.fromCharCode(64 + totalCols);
      worksheet.mergeCells(`A1:${lastColLetter}1`);
      const titleCell = worksheet.getCell("A1");
      titleCell.value = title || "Chapter Wise RnR Reports";
      styleExcelTitleRow(worksheet.getRow(1), totalCols);
      const headerRow = worksheet.getRow(2);
      styleExcelHeaderRow(headerRow, totalCols);

      worksheet.columns.forEach((column) => {
        column.width = (column.header?.length || 10) + 15;
        column.alignment = { horizontal: "center" };
      });

      setBtnExcelDisabled(true);
      const response = await axios.get(CHAPTER_WISE_ONE_TO_ONE_REPORTS_LIST, {
        params: {
          type: NOPAGINATE,
          isAdmin: 1,
        },
      });

      if (response.status === 200 && response.data.success) {
        const excelData = response.data.data.chapter_wise_one_to_one || [];
        const total = response.data.data.total_onetoone_count;

        if (excelData.length < 1) {
          toast.error("No data found!.");
          return;
        }

        excelData.forEach((singleData) => {
          worksheet.addRow({
            ...singleData,
            onetoone_count: Number(singleData.onetoone_count || 0),
          });
        });

        let totalRow = { sno: "" };
        if (hasRole([1])) totalRow.country_name = "";
        if (hasRole([1, 6])) totalRow.state_name = "";
        if (hasRole([1, 6, 7])) totalRow.region_name = "";
        totalRow.chapter_name = "Total";
        totalRow.onetoone_count = total;

        worksheet.addRow(totalRow);
        const lastRow = worksheet.lastRow;
        styleExcelSummaryRow(lastRow, totalCols);

        worksheet.eachRow({ includeEmpty: false }, (row) => {
          row._cells.forEach((singleCell) => {
            worksheet.getCell(singleCell._address).border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        });

        const buf = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buf]), `${fileName}.xlsx`);
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.message);
    } finally {
      setBtnExcelDisabled(false);
      if (workbook.getWorksheet(fileName)) {
        workbook.removeWorksheet(fileName);
      }
    }
  };

  const countFormatter = (val) => {
    const value = val?.value !== undefined ? val.value : val;
    if (value == null) return "";
    return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(value);
  };

  const chartData = data.map((item, index) => ({
    id: index,
    value: Number(item.onetoone_count || 0),
    label: item.chapter_name,
  }));

  const chartTotal = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row">
            <div className="col-lg-6">
              <h5 className="page-title">Chapter Wise RnR Reports</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span>Reports</span>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">
                    Chapter Wise RnR Reports
                  </span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                {hasPermission("reports-chapter-wise-one-to-one-list-export") && (
                  <button
                    disabled={btnExcelDisabled}
                    type="button"
                    onClick={exportAsExcel}
                    className="btn rounded-pill btn-primary excel-btn ibutton"
                  >
                    <span className="tf-icons bx bxs-spreadsheet"></span>
                    &nbsp; Excel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1">
        <div className="card icard sticky-table-card">
          <ChapterWiseReportPieChart
            loading={loading}
            chartData={chartData}
            total={chartTotal}
            valueFormatter={countFormatter}
            totalValue={totalCount}
            loaderColor="#0284c7"
          />
        </div>
      </div>
    </div>
  );
};

export default ChapterWiseOnetoOneReportsList;
